import { useParams } from 'react-router-dom';
import { useQueryData } from './useQueryData';
import { useCallback, useMemo } from 'react';
import { useGetTcoLocations, useGetTcoSubmissions } from './apiHooks';
import { TcotableValues } from 'types/TcoProducts';
import {
  ADD_UNDERLAY_FIELD_ID,
  APPLY_SAME_UNDERLAY_TO_ALL,
  tcoFieldsIds,
} from 'utils/defaultsAndConstants/constants';

import {
  tcoGeneralInformationFieldIds,
  tcoGeneralInformationFormId,
} from 'utils/defaultsAndConstants';

export interface IGenerateTcoProductTotals {
  format?: boolean;
}

export const useTcoGenerateProductTotals = () => {
  const {
    dealStructureFormId,
    costOfCapitalRateFieldId,
    qtyOfDropsFieldId,
    spectrumCostPerDropFieldId,
    spectrumScissorLiftCostFieldId,
    diyCostPerDropFieldId,
    qtyOfScissorLiftRentalsFieldId,
    diyScissorLiftCostFieldId,
    averageTravelCostPerSiteFieldId,
    noOfSitesFieldId,
    laborCostFieldId,
    laborHoursPerSiteFieldId,
    trainingOrEducationCostFieldId,
    termsFieldId,
  } = tcoFieldsIds;

  const { tcoDeviceSelections, allTcoSpectrumProducts } = useQueryData();

  const { estimateId } = useParams();

  const { data: tcoSubmissions } = useGetTcoSubmissions({
    params: { estimateId },
  });

  const { data: tcoLocations = [] } = useGetTcoLocations({
    params: { estimateId },
  });

  const applyUnderlayVia =
    tcoSubmissions?.find((s) => s.fieldId === ADD_UNDERLAY_FIELD_ID)?.answer ||
    '';

  const { totalUnderlaySpectrumCost, totalUnderlayDiyCost } = useMemo(() => {
    if (applyUnderlayVia === APPLY_SAME_UNDERLAY_TO_ALL) {
      if (tcoLocations.length === 0) {
        return { totalUnderlaySpectrumCost: 0, totalUnderlayDiyCost: 0 };
      }
      const spectrumTotal = tcoLocations[0].underlaySelections.reduce(
        (sum, sel) => sum + sel.spectrumPrice * tcoLocations[0].noOfSites,
        0
      );
      const diyTotal = tcoLocations[0].underlaySelections.reduce(
        (sum, sel) => sum + sel.diyPrice * tcoLocations[0].noOfSites,
        0
      );
      return {
        totalUnderlaySpectrumCost: spectrumTotal,
        totalUnderlayDiyCost: diyTotal,
      };
    }
    let spectrumTotal = 0;
    let diyTotal = 0;
    tcoLocations.forEach((loc) =>
      loc.underlaySelections.forEach((sel) => {
        spectrumTotal += sel.spectrumPrice * loc.noOfSites;
        diyTotal += sel.diyPrice * loc.noOfSites;
      })
    );
    return {
      totalUnderlaySpectrumCost: spectrumTotal || 0,
      totalUnderlayDiyCost: diyTotal || 0,
    };
  }, [tcoLocations, applyUnderlayVia]);

  const efficiencyLaborTransferOrReduction =
    (parseFloat(
      tcoSubmissions?.find(
        (s) =>
          s.fieldId ===
          tcoGeneralInformationFieldIds.efficiencyLaborTransferOrReductionId
      )?.answer!
    ) || 0) / 100;

  const averageOverTerm =
    parseFloat(
      tcoSubmissions?.find(
        (s) =>
          s.formId === tcoGeneralInformationFormId &&
          s.fieldId === tcoGeneralInformationFieldIds.averageTermCostId
      )?.answer!
    ) || 0;

  const term =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === termsFieldId)?.answer!
    ) || 0;

  const costOfCapitalRate =
    parseFloat(
      tcoSubmissions?.find(
        (s) =>
          s.formId === dealStructureFormId &&
          s.fieldId === costOfCapitalRateFieldId
      )?.answer!
    ) / 100 || 0;
  const qtyOfDrops =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === qtyOfDropsFieldId)?.answer!
    ) || 0;
  const spectrumCostPerDrop =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === spectrumCostPerDropFieldId)
        ?.answer!
    ) || 0;
  const spectrumScissorLiftCost =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === spectrumScissorLiftCostFieldId)
        ?.answer!
    ) || 0;
  const diyCostPerDrop =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === diyCostPerDropFieldId)?.answer!
    ) || 0;
  const qtyOfScissorLiftRentals =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === qtyOfScissorLiftRentalsFieldId)
        ?.answer!
    ) || 0;
  const diyScissorLiftCost =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === diyScissorLiftCostFieldId)
        ?.answer!
    ) || 0;
  const averageTravelCostPerSite =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === averageTravelCostPerSiteFieldId)
        ?.answer!
    ) || 0;
  const noOfSites =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === noOfSitesFieldId)?.answer!
    ) || 0;
  const laborCost =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === laborCostFieldId)?.answer!
    ) || 0;
  const laborHoursPerSite =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === laborHoursPerSiteFieldId)
        ?.answer!
    ) || 0;

  const spectrumTotalWiringCost = qtyOfDrops * spectrumCostPerDrop;
  const spectrumTotalScissorLiftCost =
    qtyOfScissorLiftRentals * spectrumScissorLiftCost;
  const diyTotalWiringCost = qtyOfDrops * diyCostPerDrop;
  const diyTotalScissorLiftCost = qtyOfScissorLiftRentals * diyScissorLiftCost;
  const diyTotalTrainingCosts =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === trainingOrEducationCostFieldId)
        ?.answer!
    ) || 0;
  const diyTotalTravelCost = averageTravelCostPerSite * noOfSites;
  const diyTotalLaborCost = laborCost * laborHoursPerSite * noOfSites;
  const diyImplementationCost =
    diyTotalWiringCost +
    diyTotalScissorLiftCost +
    diyTotalTravelCost +
    diyTotalLaborCost;
  const spectrumImplementationCost =
    spectrumTotalWiringCost + spectrumTotalScissorLiftCost;

  const getFamilyTypeById = (
    id: string,
    products: Array<{ id: string; familyType: string }>
  ): string => {
    const product = products.find((p) => p.id === id);
    return product ? product.familyType : '';
  };

  const aggregatedCounts = tcoDeviceSelections?.reduce((acc, selection) => {
    const deviceFamilyType = getFamilyTypeById(
      selection.spectrumId,
      allTcoSpectrumProducts
    ).toLowerCase();
    if (deviceFamilyType) {
      acc[deviceFamilyType] = (acc[deviceFamilyType] || 0) + selection.quantity;
    }
    return acc;
  }, {} as Record<string, number>);
  const edgeCount = aggregatedCounts?.['edge'] || 0;
  const switchCount = aggregatedCounts?.['switch'] || 0;
  const accessPointCount = aggregatedCounts?.['access point'] || 0;
  const cameraCount = aggregatedCounts?.['camera'] || 0;

  // Spectrum Operational Costs
  const spectrumConfigEdgeOperationalCost =
    ((averageOverTerm * 4 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    edgeCount;
  const spectrumConfigSwitchOperationalCost =
    ((averageOverTerm * 2 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    switchCount;
  const spectrumConfigApOperationalCost =
    ((averageOverTerm * 2 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    accessPointCount;
  const spectrumConfigCameraOperationalCost =
    ((averageOverTerm * 2 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    cameraCount;
  const spectrumMonitoringEdgeOperationalCost =
    ((averageOverTerm * 4 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    edgeCount;
  const spectrumMonitoringSwitchOperationalCost =
    ((averageOverTerm * 2 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    switchCount;
  const spectrumMonitoringApOperationalCost =
    ((averageOverTerm * 0.5 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    accessPointCount;
  const spectrumMonitoringCameraOperationalCost =
    ((averageOverTerm * 2 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    cameraCount;
  const spectrumTroubleshootingEdgeOperationalCost =
    ((averageOverTerm * 3 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    edgeCount;
  const spectrumTroubleshootingSwitchOperationalCost =
    ((averageOverTerm * 1 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    switchCount;
  const spectrumTroubleshootingApOperationalCost =
    ((averageOverTerm * 1 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    accessPointCount;
  const spectrumTroubleshootingCameraOperationalCost =
    ((averageOverTerm * 0.5 * (1 - efficiencyLaborTransferOrReduction)) / 50) *
    4 *
    cameraCount;

  // DIY Operational Costs

  const diyConfigEdgeOperationalCost =
    ((averageOverTerm * 4) / 50) * 4 * edgeCount;
  const diyConfigSwitchOperationalCost =
    ((averageOverTerm * 2) / 50) * 4 * switchCount;
  const diyConfigApOperationalCost =
    ((averageOverTerm * 2) / 50) * 4 * accessPointCount;
  const diyConfigCameraOperationalCost =
    ((averageOverTerm * 2) / 50) * 4 * cameraCount;
  const diyMonitoringEdgeOperationalCost =
    ((averageOverTerm * 4) / 50) * 4 * edgeCount;
  const diyMonitoringSwitchOperationalCost =
    ((averageOverTerm * 2) / 50) * 4 * switchCount;
  const diyMonitoringApOperationalCost =
    ((averageOverTerm * 0.5) / 50) * 4 * accessPointCount;
  const diyMonitoringCameraOperationalCost =
    ((averageOverTerm * 2) / 50) * 4 * cameraCount;
  const diyTroubleshootingEdgeOperationalCost =
    ((averageOverTerm * 3) / 50) * 4 * edgeCount;
  const diyTroubleshootingSwitchOperationalCost =
    ((averageOverTerm * 1) / 50) * 4 * switchCount;
  const diyTroubleshootingApOperationalCost =
    ((averageOverTerm * 1) / 50) * 4 * accessPointCount;
  const diyTroubleshootingCameraOperationalCost =
    ((averageOverTerm * 0.5) / 50) * 4 * cameraCount;

  const generateTcoProductTotals = useCallback(
    ({ format = false }: IGenerateTcoProductTotals) => {
      const defaultOutputs = {
        diyEquipmentCosts: 0,
        diyCostOfCapital: 0,
        diyImplementationCosts: 0,
        totalInstallationCost: 0,
        diyTraining: 0,
        spectrumEquipmentCosts: 0,
        spectrumCostOfCapital: 0,
        spectrumImplementationCosts: 0,
        spectrumTraining: 0,
        spectrumConfigEdgeOperationalCost: 0,
        spectrumConfigSwitchOperationalCost: 0,
        spectrumConfigCameraOperationalCost: 0,
        spectrumConfigApOperationalCost: 0,
        spectrumMonitoringEdgeOperationalCost: 0,
        spectrumMonitoringSwitchOperationalCost: 0,
        spectrumMonitoringApOperationalCost: 0,
        spectrumMonitoringCameraOperationalCost: 0,
        spectrumTroubleshootingEdgeOperationalCost: 0,
        spectrumTroubleshootingSwitchOperationalCost: 0,
        spectrumTroubleshootingApOperationalCost: 0,
        spectrumTroubleshootingCameraOperationalCost: 0,
        diyConfigEdgeOperationalCost: 0,
        diyConfigSwitchOperationalCost: 0,
        diyConfigCameraOperationalCost: 0,
        diyConfigApOperationalCost: 0,
        diyMonitoringEdgeOperationalCost: 0,
        diyMonitoringSwitchOperationalCost: 0,
        diyMonitoringApOperationalCost: 0,
        diyMonitoringCameraOperationalCost: 0,
        diyTroubleshootingEdgeOperationalCost: 0,
        diyTroubleshootingSwitchOperationalCost: 0,
        diyTroubleshootingApOperationalCost: 0,
        diyTroubleshootingCameraOperationalCost: 0,
        spectrumConfigurationCostTotal: 0,
        spectrumMonitoringCostTotal: 0,
        spectrumTroubleshootingCostTotal: 0,
        diyConfigurationCostTotal: 0,
        diyMonitoringCostTotal: 0,
        diyTroubleshootingCostTotal: 0,
        spectrumMonthlyDeviceManagementFee: 0,
        diyMonthlyDeviceManagementFee: 0,
        licenseManagementFee: 0,
      };

      const totals =
        tcoDeviceSelections?.reduce(
          (acc: TcotableValues<number>, selection) => {
            acc.diyEquipmentCosts +=
              selection.hardwareCost * selection.quantity +
              selection.licenseCost * selection.quantity;
            acc.spectrumEquipmentCosts = 0;
            acc.totalInstallationCost +=
              selection.installationCost * selection.quantity;
            acc.spectrumCostOfCapital = 0;
            acc.spectrumTraining = 0;
            return acc;
          },
          defaultOutputs
        ) || defaultOutputs;

      totals.diyCostOfCapital = totals.diyEquipmentCosts * costOfCapitalRate;
      totals.diyImplementationCosts = diyImplementationCost;
      totals.diyTraining = diyTotalTrainingCosts;
      totals.spectrumImplementationCosts =
        spectrumImplementationCost + totals.totalInstallationCost;
      totals.spectrumMonthlyDeviceManagementFee =
        tcoDeviceSelections.reduce(
          (acc, selection) => acc + selection.monthlyCost * selection.quantity,
          0
        ) + totalUnderlaySpectrumCost;
      totals.diyMonthlyDeviceManagementFee = totalUnderlayDiyCost;

      totals.licenseManagementFee =
        (tcoDeviceSelections.reduce(
          (acc, selection) => acc + selection.quantity,
          0
        ) /
          (term * 12)) *
        averageOverTerm;

      // Spectrum Configuration Monthly Operational costs
      totals.spectrumConfigEdgeOperationalCost =
        spectrumConfigEdgeOperationalCost;
      totals.spectrumConfigSwitchOperationalCost =
        spectrumConfigSwitchOperationalCost;
      totals.spectrumConfigApOperationalCost = spectrumConfigApOperationalCost;
      totals.spectrumConfigCameraOperationalCost =
        spectrumConfigCameraOperationalCost;

      // Spectrum Monitoring Monthly Operational costs
      totals.spectrumMonitoringEdgeOperationalCost =
        spectrumMonitoringEdgeOperationalCost;
      totals.spectrumMonitoringSwitchOperationalCost =
        spectrumMonitoringSwitchOperationalCost;
      totals.spectrumMonitoringApOperationalCost =
        spectrumMonitoringApOperationalCost;
      totals.spectrumMonitoringCameraOperationalCost =
        spectrumMonitoringCameraOperationalCost;

      // Spectrum Troubleshooting Monthly Operational costs
      totals.spectrumTroubleshootingEdgeOperationalCost =
        spectrumTroubleshootingEdgeOperationalCost;
      totals.spectrumTroubleshootingSwitchOperationalCost =
        spectrumTroubleshootingSwitchOperationalCost;
      totals.spectrumTroubleshootingApOperationalCost =
        spectrumTroubleshootingApOperationalCost;
      totals.spectrumTroubleshootingCameraOperationalCost =
        spectrumTroubleshootingCameraOperationalCost;

      // DIY Configuration Monthly Operational costs
      totals.diyConfigEdgeOperationalCost = diyConfigEdgeOperationalCost;
      totals.diyConfigSwitchOperationalCost = diyConfigSwitchOperationalCost;
      totals.diyConfigApOperationalCost = diyConfigApOperationalCost;
      totals.diyConfigCameraOperationalCost = diyConfigCameraOperationalCost;

      // DIY Monitoring Monthly Operational costs
      totals.diyMonitoringEdgeOperationalCost =
        diyMonitoringEdgeOperationalCost;
      totals.diyMonitoringSwitchOperationalCost =
        diyMonitoringSwitchOperationalCost;
      totals.diyMonitoringApOperationalCost = diyMonitoringApOperationalCost;
      totals.diyMonitoringCameraOperationalCost =
        diyMonitoringCameraOperationalCost;

      // DIY Troubleshooting Monthly Operational costs
      totals.diyTroubleshootingEdgeOperationalCost =
        diyTroubleshootingEdgeOperationalCost;
      totals.diyTroubleshootingSwitchOperationalCost =
        diyTroubleshootingSwitchOperationalCost;
      totals.diyTroubleshootingApOperationalCost =
        diyTroubleshootingApOperationalCost;
      totals.diyTroubleshootingCameraOperationalCost =
        diyTroubleshootingCameraOperationalCost;

      // Round all the values to 2 decimal places
      (Object.keys(totals) as Array<keyof TcotableValues<number>>).forEach(
        (key) => {
          totals[key] = parseFloat(totals[key].toFixed(2));
        }
      );

      totals.diyConfigurationCostTotal = parseFloat(
        (
          totals.diyConfigEdgeOperationalCost +
          totals.diyConfigSwitchOperationalCost +
          totals.diyConfigApOperationalCost +
          totals.diyConfigCameraOperationalCost
        ).toFixed(2)
      );
      totals.diyTroubleshootingCostTotal = parseFloat(
        (
          totals.diyTroubleshootingEdgeOperationalCost +
          totals.diyTroubleshootingSwitchOperationalCost +
          totals.diyTroubleshootingApOperationalCost +
          totals.diyTroubleshootingCameraOperationalCost
        ).toFixed(2)
      );
      totals.diyMonitoringCostTotal = parseFloat(
        (
          totals.diyMonitoringEdgeOperationalCost +
          totals.diyMonitoringSwitchOperationalCost +
          totals.diyMonitoringApOperationalCost +
          totals.diyMonitoringCameraOperationalCost
        ).toFixed(2)
      );
      totals.spectrumConfigurationCostTotal = parseFloat(
        (
          totals.spectrumConfigEdgeOperationalCost +
          totals.spectrumConfigSwitchOperationalCost +
          totals.spectrumConfigApOperationalCost +
          totals.spectrumConfigCameraOperationalCost
        ).toFixed(2)
      );
      totals.spectrumTroubleshootingCostTotal = parseFloat(
        (
          totals.spectrumTroubleshootingEdgeOperationalCost +
          totals.spectrumTroubleshootingSwitchOperationalCost +
          totals.spectrumTroubleshootingApOperationalCost +
          totals.spectrumTroubleshootingCameraOperationalCost
        ).toFixed(2)
      );
      totals.spectrumMonitoringCostTotal = parseFloat(
        (
          totals.spectrumMonitoringEdgeOperationalCost +
          totals.spectrumMonitoringSwitchOperationalCost +
          totals.spectrumMonitoringApOperationalCost +
          totals.spectrumMonitoringCameraOperationalCost
        ).toFixed(2)
      );
      totals.spectrumConfigurationCostTotal = parseFloat(
        (
          totals.spectrumConfigEdgeOperationalCost +
          totals.spectrumConfigSwitchOperationalCost +
          totals.spectrumConfigApOperationalCost +
          totals.spectrumConfigCameraOperationalCost
        ).toFixed(2)
      );

      return totals;
    },
    [
      averageOverTerm,
      term,
      tcoDeviceSelections,
      costOfCapitalRate,
      diyImplementationCost,
      diyTotalTrainingCosts,
      spectrumImplementationCost,
      spectrumConfigEdgeOperationalCost,
      spectrumConfigSwitchOperationalCost,
      spectrumConfigApOperationalCost,
      spectrumConfigCameraOperationalCost,
      spectrumMonitoringEdgeOperationalCost,
      spectrumMonitoringSwitchOperationalCost,
      spectrumMonitoringApOperationalCost,
      spectrumMonitoringCameraOperationalCost,
      spectrumTroubleshootingEdgeOperationalCost,
      spectrumTroubleshootingSwitchOperationalCost,
      spectrumTroubleshootingApOperationalCost,
      spectrumTroubleshootingCameraOperationalCost,
      diyConfigEdgeOperationalCost,
      diyConfigSwitchOperationalCost,
      diyConfigApOperationalCost,
      diyConfigCameraOperationalCost,
      diyMonitoringEdgeOperationalCost,
      diyMonitoringSwitchOperationalCost,
      diyMonitoringApOperationalCost,
      diyMonitoringCameraOperationalCost,
      diyTroubleshootingEdgeOperationalCost,
      diyTroubleshootingSwitchOperationalCost,
      diyTroubleshootingApOperationalCost,
      diyTroubleshootingCameraOperationalCost,
      totalUnderlayDiyCost,
      totalUnderlaySpectrumCost,
    ]
  );

  return {
    generateTcoProductTotals,
  };
};
